import { useRouter } from '@tanstack/react-router'
import { useEffect } from 'react'
import { useAuth } from 'react-oidc-context'

import { HOME_ROUTE, LOGIN_ROUTE, type LoginState, safeRedirectPath } from '@/lib/auth'

/**
 * Costura o ciclo de vida da sessão Gov.br ao roteador. Montado uma única vez,
 * na raiz.
 *
 * Cobre as duas pontas que o `AuthProvider` sozinho não resolve: encaminhar quem
 * está na raiz — para o login, ou para a rota pedida antes dele — e derrubar a
 * sessão quando o token expira de vez.
 */
export function useGovBrSession(): void {
  const auth = useAuth()
  const router = useRouter()

  useEffect(() => {
    // O `automaticSilentRenew` já tentou renovar; chegar aqui significa que a
    // sessão acabou. Limpar o usuário faz o RequireAuth mandar ao login em vez
    // de deixar a tela chamando a API com um token morto e colhendo 401.
    return auth.events.addAccessTokenExpired(() => {
      void auth.removeUser()
    })
  }, [auth])

  useEffect(() => {
    // O `redirect_uri` registrado no Keycloak é a raiz, então é lá que o browser
    // aterrissa depois do IDP — e é lá que também chega quem abre a aplicação
    // pela primeira vez. A raiz não tem tela: ela encaminha. Estar em outra rota
    // significa que o usuário navegou por conta própria, e sequestrar a
    // navegação aí seria um bug.
    if (router.state.location.pathname !== '/') return

    // Enquanto o oidc-client-ts lê o sessionStorage, `isAuthenticated` é falso;
    // decidir agora mandaria ao login quem já tem sessão.
    if (auth.isLoading) return

    if (!auth.isAuthenticated) {
      router.history.replace(LOGIN_ROUTE)
      return
    }

    const redirectTo = safeRedirectPath((auth.user?.state as LoginState | undefined)?.redirectTo)

    // Sem rota guardada — entrada direta, ou um `state` que a normalização
    // recusou — o destino é a lista de cadastros, não a raiz, que devolveria
    // este mesmo efeito.
    router.history.replace(redirectTo === '/' ? HOME_ROUTE : redirectTo)
  }, [auth.isLoading, auth.isAuthenticated, auth.user, router])
}
